import {
    createContext,
    ReactNode,
    FC,
    useEffect,
    useState,
    useCallback,
    useRef,
    SetStateAction,
    Dispatch,
} from 'react';
import { useFragment, graphql } from 'react-relay';
import { useWeb3EagerConnect } from 'dibs-crypto-utils/exports/useWeb3EagerConnect';
import { useWeb3Connection } from 'dibs-crypto-utils/exports/useWeb3Connection';
import { isV2Auction } from 'dibs-crypto-utils/exports/isV2Auction';
import { getAuctionContract, getAuctionV2Contract } from 'dibs-crypto-utils/exports/getContract';

import type { FirstDibsAuction, FirstDibsAuctionV2 } from '1stdibs-contracts/typechain';
import { getChainIdFromEnv } from 'dibs-crypto-utils/exports/supportedChains';
import { NftPdpContractContext_nftItem$key } from './__generated__/NftPdpContractContext_nftItem.graphql';

type NftPdpContractContextProps = {
    children: ReactNode;
    nftItem: NftPdpContractContext_nftItem$key;
};

type NftPdpContractContextType = {
    minBidIncrement: number;
    buyerPremium: number;
    auctionContract: FirstDibsAuction | FirstDibsAuctionV2 | null;
    auctionContractV1: FirstDibsAuction | null;
    isAuctionGloballyPaused: boolean | null;
    setIsAuctionGloballyPaused: Dispatch<SetStateAction<boolean | null>>;
    isV2AuctionContract: boolean;
};

export const NftPdpContractContext = createContext<NftPdpContractContextType>({
    minBidIncrement: 0,
    buyerPremium: 0,
    auctionContract: null,
    auctionContractV1: null,
    isAuctionGloballyPaused: null,
    setIsAuctionGloballyPaused: () => null,
    isV2AuctionContract: false,
});

export const NftPdpContractContextProvider: FC<NftPdpContractContextProps> = ({
    children,
    nftItem: nftItemRef,
}) => {
    const nftItem = useFragment(
        graphql`
            fragment NftPdpContractContext_nftItem on NftItem {
                mostRecentAuction {
                    auctionId
                    contractAddress {
                        address
                    }
                }
            }
        `,
        nftItemRef
    );
    const [auctionContract, setAuctionContract] = useState<
        FirstDibsAuction | FirstDibsAuctionV2 | null
    >(null);
    const [auctionContractV1, setAuctionContractV1] = useState<FirstDibsAuction | null>(null); // store V1 separately to check for bidder role, which is only on V1 auction contract
    const isV2AuctionContract = isV2Auction(nftItem?.mostRecentAuction?.contractAddress?.address);

    const [auctionSettings, setAuctionSettings] = useState<{
        minBidIncrement: number;
        buyerPremium: number;
    }>({ minBidIncrement: 0, buyerPremium: 0 });
    const [isAuctionGloballyPaused, setIsAuctionGloballyPaused] = useState<boolean | null>(null);

    const { account, library } = useWeb3Connection();
    useWeb3EagerConnect();
    const contractConnectedAccount = useRef<string | null>(null);
    const chainId = getChainIdFromEnv();

    const fetchMinBidIncrement = useCallback(
        async (contract: FirstDibsAuction | FirstDibsAuctionV2) => {
            const auctionId = nftItem?.mostRecentAuction?.auctionId;
            if (auctionId) {
                try {
                    const auctionStruct = await contract.auctions(auctionId);
                    const auctionStructSettings = auctionStruct?.settings;
                    const minBidIncrement = auctionStructSettings?.minimumBidIncrement;
                    const buyerPremium = auctionStructSettings?.buyerPremium;
                    setAuctionSettings({
                        minBidIncrement: minBidIncrement || 0,
                        buyerPremium: buyerPremium || 0,
                    });
                } catch (e) {
                    // do something with error?
                }
            }
        },
        [nftItem]
    );

    const fetchGloballyPaused = useCallback(
        async (contract: FirstDibsAuction | FirstDibsAuctionV2) => {
            const auctionId = nftItem?.mostRecentAuction?.auctionId;
            if (auctionId) {
                try {
                    const isGloballyPaused = await contract.globalPaused();
                    setIsAuctionGloballyPaused(isGloballyPaused);
                } catch (e) {
                    setIsAuctionGloballyPaused(false);
                }
            }
        },
        [nftItem]
    );

    const fetchAuctionContract = useCallback(async () => {
        if (chainId && library && !auctionContract) {
            const auctionV1 = await getAuctionContract(library, chainId);
            const auctionV2 = await getAuctionV2Contract(library, chainId);
            const contract = isV2AuctionContract && auctionV2 ? auctionV2 : auctionV1;
            setAuctionContract(contract);
            setAuctionContractV1(auctionV1);
            if (contract) {
                fetchMinBidIncrement(contract);
                fetchGloballyPaused(contract);
            }
        }
        if (library && account && auctionContract) {
            setAuctionContract(auctionContract.connect(library.getSigner(account)));
        }
    }, [
        account,
        auctionContract,
        chainId,
        fetchGloballyPaused,
        fetchMinBidIncrement,
        library,
        isV2AuctionContract,
    ]);
    useEffect(() => {
        if (!auctionContract) {
            fetchAuctionContract();
        }
    }, [auctionContract, fetchAuctionContract]);

    useEffect(() => {
        if (account && library && auctionContract && contractConnectedAccount.current !== account) {
            setAuctionContract(auctionContract.connect(library.getSigner(account)));
            contractConnectedAccount.current = account;
        }
    }, [account, auctionContract, library]);

    return (
        <NftPdpContractContext.Provider
            value={{
                minBidIncrement: auctionSettings.minBidIncrement,
                buyerPremium: auctionSettings.buyerPremium,
                auctionContract,
                auctionContractV1,
                isAuctionGloballyPaused,
                setIsAuctionGloballyPaused,
                isV2AuctionContract,
            }}
        >
            {children}
        </NftPdpContractContext.Provider>
    );
};
